import { useMemo, useState } from 'react'
import { CALC_FORMS, NEC_EDITION, defaultValues, type FormResult, type CalculatorId } from '@wirecoder/calc'

const IOS_URL = import.meta.env.VITE_IOS_APP_URL || '#'
const ANDROID_URL = import.meta.env.VITE_ANDROID_APP_URL || '#'

export function WebCalculator({ calcId }: { calcId: CalculatorId }) {
  const config = CALC_FORMS[calcId]
  const [values, setValues] = useState<Record<string, string>>(() => defaultValues(calcId))

  const result: FormResult = useMemo(() => config.compute(values), [config, values])

  const set = (key: string, value: string) => setValues((v) => ({ ...v, [key]: value }))

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        {config.fields.map((field) => (
          <label key={field.key} className="block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">
              {field.label}
              {field.kind === 'number' && field.unit ? (
                <span className="font-normal text-slate-400"> ({field.unit})</span>
              ) : null}
            </span>
            {field.kind === 'select' ? (
              <select
                value={values[field.key]}
                onChange={(e) => set(field.key, e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-lg text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {field.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                inputMode="decimal"
                step={field.step ?? 'any'}
                value={values[field.key]}
                onChange={(e) => set(field.key, e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-lg text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            )}
          </label>
        ))}
      </div>

      {/* Results — recompute live, no submit button (job-site fast). */}
      <div className="mt-6">
        {result.ok ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-sm font-medium uppercase tracking-wide text-emerald-700">
                {result.headline.label}
              </span>
              <span
                className={`text-3xl font-extrabold ${
                  result.headline.warn ? 'text-orange-600' : 'text-emerald-700'
                }`}
              >
                {result.headline.value}
              </span>
            </div>
            <dl className="mt-4 divide-y divide-emerald-100 border-t border-emerald-100">
              {result.rows.map((row, i) => (
                <div key={i} className="flex justify-between gap-4 py-2 text-sm">
                  <dt className="text-slate-600">{row.label}</dt>
                  <dd className={`font-semibold ${row.warn ? 'text-orange-600' : 'text-slate-900'}`}>
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
            {result.error}
          </div>
        )}
      </div>

      {/* NEC edition + liability footer (design doc §0) */}
      <p className="mt-4 text-center text-xs text-slate-400">
        Based on {NEC_EDITION}. Estimate only — verify against your AHJ and the current NEC.
      </p>

      {/* Soft install CTA — the funnel goal */}
      <div className="mt-6 rounded-xl bg-slate-900 p-5 text-center">
        <p className="text-sm font-semibold text-white">
          Get all 5 calculators offline + save your jobs
        </p>
        <p className="mt-1 text-xs text-slate-300">Voltage drop, conduit fill, ampacity, box fill, bending — one app.</p>
        <div className="mt-3 flex justify-center gap-3">
          <a
            href={IOS_URL}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
          >
            App Store
          </a>
          <a
            href={ANDROID_URL}
            className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Google Play
          </a>
        </div>
      </div>
    </div>
  )
}
